/* ════════════ THE 1 SPD SNAKE ════════════
   The Python dossier's atmosphere: a pixel snake slithering across
   the screen on a lazy sine wave, very slowly. It is not in a hurry.
   It has the GIL. Behind it, sparse >>> prompts drift like plankton. */

import { fillBars } from "./statbars";
import { onPage } from "./router";

const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;

function snake(signal: AbortSignal) {
  const c = document.querySelector<HTMLCanvasElement>("#snake")!;
  const ctx = c.getContext("2d")!;
  const SEG = 8; // segment size in px
  const LEN = 26; // body segments

  type P = { x: number; y: number };
  let trail: P[] = [];
  let head: P = { x: -LEN * SEG, y: 0 };
  let baseY = 0;
  let t = 0;

  type Prompt = { x: number; y: number; vy: number; alpha: number };
  let prompts: Prompt[] = [];

  const makePrompt = (anywhere = false): Prompt => ({
    x: Math.random() * c.width,
    y: anywhere ? Math.random() * c.height : c.height + 10,
    vy: 0.08 + Math.random() * 0.18,
    alpha: 0.12 + Math.random() * 0.2,
  });

  const seed = () => {
    c.width = innerWidth;
    c.height = innerHeight;
    baseY = c.height * (0.25 + Math.random() * 0.5);
    head = { x: -LEN * SEG * 1.5, y: baseY };
    trail = Array.from({ length: LEN }, (_, i) => ({ x: head.x - i * SEG, y: baseY }));
    prompts = Array.from({ length: Math.min(16, Math.floor(c.width / 90)) }, () => makePrompt(true));
  };
  seed();
  addEventListener("resize", seed, { signal });

  const draw = () => {
    ctx.clearRect(0, 0, c.width, c.height);
    // drifting REPL prompts
    ctx.font = '12px "IBM Plex Mono", monospace';
    for (const p of prompts) {
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = "#4d7ba8";
      ctx.fillText(">>>", p.x | 0, p.y | 0);
    }
    // the snake: yellow body, blue belly stripe, head with an eye
    for (let i = trail.length - 1; i >= 0; i--) {
      const s = trail[i]!;
      const fade = 1 - (i / trail.length) * 0.5;
      ctx.globalAlpha = 0.75 * fade;
      ctx.fillStyle = i % 5 < 3 ? "#ffd43b" : "#306998";
      // quantize to the pixel grid so it reads as retro
      ctx.fillRect((s.x / 4 | 0) * 4, (s.y / 4 | 0) * 4, SEG, SEG);
    }
    const h = trail[0]!;
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "#ffd43b";
    ctx.fillRect((h.x / 4 | 0) * 4, (h.y / 4 | 0) * 4, SEG + 2, SEG);
    ctx.fillStyle = "#060a10";
    ctx.fillRect(((h.x + SEG - 2) / 4 | 0) * 4, ((h.y + 2) / 4 | 0) * 4, 2, 2); // the eye
    ctx.globalAlpha = 1;
  };

  if (REDUCED) {
    head.x = c.width * 0.3;
    trail = Array.from({ length: LEN }, (_, i) => ({
      x: head.x - i * SEG,
      y: baseY + Math.sin((head.x - i * SEG) * 0.02) * 24,
    }));
    draw();
    return;
  }
  const tick = () => {
    if (signal.aborted) return;
    t += 0.016;
    // 1 SPD. it was benchmarked. it lost.
    head.x += 0.55;
    head.y = baseY + Math.sin(head.x * 0.018) * 28;
    if (head.x > c.width + LEN * SEG) {
      head.x = -LEN * SEG * 1.5;
      baseY = c.height * (0.2 + Math.random() * 0.6);
    }
    trail.unshift({ x: head.x, y: head.y });
    if (trail.length > LEN) trail.pop();

    for (const p of prompts) {
      p.y -= p.vy;
      if (p.y < -10) Object.assign(p, makePrompt());
    }
    draw();
    requestAnimationFrame(tick);
  };
  tick();
}

onPage((signal) => {
  snake(signal);
  fillBars();
});
